<template>
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    width="960px"
    :fullscreen="isSmallScreen"
    top="5vh"
    append-to-body
    :close-on-click-modal="false"
    custom-class="bulk-feature-dialog"
    data-cy="bulk-feature-dialog"
    @close="$emit('close')"
  >
    <div class="mb-3">
      <el-tag v-for="name in visibleTenantNames" :key="name" size="mini" type="info" class="mr-1 mb-1">{{ name }}</el-tag>
      <el-tag v-if="hiddenTenantCount > 0" size="mini" type="info" class="mb-1">+{{ hiddenTenantCount }} more</el-tag>
      <p v-if="unloadedTenantCount > 0" class="text-xs text-gray-600 mt-1">
        Current state is shown for {{ knownCount }} of {{ tenantIds.length }} selected tenants (the rest are on other pages).
      </p>
    </div>

    <!-- STEP 1: CHOOSE CHANGES -->
    <template v-if="step === 'choose'">
      <div class="bulk-feature-toolbar mb-3">
        <el-input
          v-model="search"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search features"
          class="bulk-feature-search"
          data-cy="bulk-feature-search"
        />
        <el-checkbox v-model="onlyChanged" data-cy="bulk-feature-only-changed">Show only rows with changes</el-checkbox>
      </div>

      <div class="bulk-feature-table-wrapper">
        <table class="bulk-feature-table w-full">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Access now</th>
              <th>Set Access</th>
              <th>Enabled now</th>
              <th>Set Enabled</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row.feature.key"
              :class="{ 'is-changed': row.changed }"
              :data-cy="`bulk-feature-row-${row.feature.key}`"
            >
              <td class="font-medium bulk-feature-name">{{ row.feature.label }}</td>
              <td data-label="Access now"><span :class="stateClass(row.state.accessOn)">{{ stateText(row.state.accessOn) }}</span></td>
              <td data-label="Set Access">
                <el-radio-group :value="row.resolved.access" size="mini" @input="value => setChoice(row.feature.key, 'access', value)">
                  <el-radio-button :label="NO_CHANGE">No change</el-radio-button>
                  <el-radio-button :label="ON">On</el-radio-button>
                  <el-radio-button :label="OFF" class="bulk-feature-off">Off</el-radio-button>
                </el-radio-group>
                <div v-if="row.resolved.accessForced && !(knownCount && row.state.accessOn === knownCount)" class="text-xs text-orange-500 mt-1">Turns on: Enabled needs Access</div>
                <div v-if="row.feature.requires && row.resolved.access === ON" class="text-xs text-orange-500 mt-1" :data-cy="`bulk-feature-requires-${row.feature.key}`">Also turns on {{ row.feature.requires.label }}</div>
              </td>
              <td data-label="Enabled now"><span :class="stateClass(row.state.enabledOn)">{{ stateText(row.state.enabledOn) }}</span></td>
              <td data-label="Set Enabled">
                <el-radio-group :value="row.resolved.enabled" size="mini" @input="value => setChoice(row.feature.key, 'enabled', value)">
                  <el-radio-button :label="NO_CHANGE" :disabled="row.resolved.access === OFF">No change</el-radio-button>
                  <el-radio-button :label="ON" :disabled="row.resolved.access === OFF">On</el-radio-button>
                  <el-radio-button :label="OFF" class="bulk-feature-off">Off</el-radio-button>
                </el-radio-group>
                <div v-if="row.resolved.enabledForced && row.state.enabledOn !== 0" class="text-xs text-orange-500 mt-1">Turns off with Access</div>
              </td>
            </tr>
            <tr v-if="visibleRows.length === 0">
              <td colspan="5" class="text-center text-gray-600">No features match.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- STEP 2: REVIEW -->
    <template v-else>
      <div v-if="turnsAnythingOff" class="bulk-feature-warning mb-3" data-cy="bulk-feature-off-warning">
        <i class="el-icon-warning-outline"></i>
        {{ offCount }} {{ offCount === 1 ? 'change turns' : 'changes turn' }} a feature off. Users in these tenants lose access right away.
      </div>
      <ul class="bulk-feature-review" data-cy="bulk-feature-review">
        <li v-for="line in reviewLines" :key="line.key">
          <el-tag size="mini" :type="line.turnsOff ? 'danger' : ''">{{ line.turnsOff ? 'OFF' : 'ON' }}</el-tag>
          <span class="font-bold ml-1">{{ line.label }}:</span>
          {{ describeLine(line) }}
        </li>
      </ul>
      <p class="text-xs text-gray-600 mt-3">Changes can take a few seconds to show in the list.</p>
    </template>

    <div slot="footer" class="bulk-feature-footer">
      <span class="text-sm text-left bulk-feature-summary">
        <b>{{ changeCount }} {{ changeCount === 1 ? 'change' : 'changes' }}</b> · {{ tenantCountText }}
        <span v-if="turnsAnythingOff" class="text-red-500"> · {{ offCount }} {{ offCount === 1 ? 'turns' : 'turn' }} a feature off</span>
      </span>
      <span v-if="step === 'choose'" class="bulk-feature-actions">
        <el-button size="small" @click="$emit('close')">Cancel</el-button>
        <el-button size="small" type="primary" :disabled="changeCount === 0" data-cy="bulk-feature-review-btn" @click="step = 'review'">Review changes</el-button>
      </span>
      <span v-else class="bulk-feature-actions">
        <el-button size="small" @click="step = 'choose'">Back</el-button>
        <el-button size="small" :type="turnsAnythingOff ? 'danger' : 'primary'" data-cy="bulk-feature-apply-btn" @click="apply">
          Apply {{ changeCount }} {{ changeCount === 1 ? 'change' : 'changes' }}
        </el-button>
      </span>
    </div>
  </el-dialog>
</template>

<script>
import {
  BULK_TOGGLE_FEATURES, NO_CHANGE, ON, OFF,
  emptySelections, resolveSelection, isChanged, buildChanges, countFeatureState, describeChanges,
} from '../bulkFeatureRegistry'

const MAX_TENANT_TAGS = 5
// Below this width the dialog goes fullscreen and each feature row stacks into a card
const SMALL_SCREEN_QUERY = '(max-width: 767px)'

export default {
  name: 'BulkFeatureUpdateDialog',
  props: {
    visible: { type: Boolean, default: false },
    tenantIds: { type: Array, default: () => [] },
    tenantRows: { type: Array, default: () => [] },
  },
  data() {
    return {
      NO_CHANGE,
      ON,
      OFF,
      step: 'choose',
      search: '',
      onlyChanged: false,
      selections: emptySelections(),
      isSmallScreen: false,
    }
  },
  computed: {
    knownRows() {
      const ids = new Set(this.tenantIds)
      return this.tenantRows.filter(row => row && ids.has(row.id))
    },
    knownCount() {
      return this.knownRows.length
    },
    unloadedTenantCount() {
      return Math.max(this.tenantIds.length - this.knownCount, 0)
    },
    dialogTitle() {
      return this.step === 'review'
        ? `Review changes for ${this.tenantCountText}`
        : `Update features for ${this.tenantCountText}`
    },
    tenantCountText() {
      const count = this.tenantIds.length
      return `${count} ${count === 1 ? 'tenant' : 'tenants'}`
    },
    visibleTenantNames() {
      return this.knownRows.slice(0, MAX_TENANT_TAGS).map(row => row.companyName || row.id)
    },
    hiddenTenantCount() {
      return Math.max(this.tenantIds.length - this.visibleTenantNames.length, 0)
    },
    rows() {
      return BULK_TOGGLE_FEATURES.map(feature => {
        const selection = this.selections[feature.key]
        return {
          feature,
          resolved: resolveSelection(selection),
          changed: isChanged(selection),
          state: countFeatureState(feature, this.knownRows),
        }
      })
    },
    visibleRows() {
      const term = this.search.trim().toLowerCase()
      return this.rows.filter(row =>
        (!term || row.feature.label.toLowerCase().includes(term)) && (!this.onlyChanged || row.changed)
      )
    },
    changes() {
      return buildChanges(this.selections)
    },
    reviewLines() {
      return describeChanges(this.selections, this.knownRows)
    },
    changeCount() {
      return this.reviewLines.length
    },
    offCount() {
      return this.reviewLines.filter(line => line.turnsOff).length
    },
    turnsAnythingOff() {
      return this.offCount > 0
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) this.reset()
    },
  },
  mounted() {
    this.mediaQuery = window.matchMedia(SMALL_SCREEN_QUERY)
    this.isSmallScreen = this.mediaQuery.matches
    this.onMediaChange = (event) => { this.isSmallScreen = event.matches }
    this.mediaQuery.addEventListener('change', this.onMediaChange)
  },
  beforeDestroy() {
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
  },
  methods: {
    reset() {
      this.step = 'choose'
      this.search = ''
      this.onlyChanged = false
      this.selections = emptySelections()
    },
    stateText(onCount) {
      if (!this.knownCount) return 'Unknown'
      if (onCount === 0) return 'None'
      if (onCount === this.knownCount) return this.knownCount === 1 ? 'On' : `All ${this.knownCount}`
      return `${onCount} of ${this.knownCount}`
    },
    stateClass(onCount) {
      const mixed = onCount > 0 && onCount < this.knownCount
      return mixed ? 'text-orange-500 text-xs' : 'text-gray-600 text-xs'
    },
    describeLine(line) {
      return line.parts
        .filter(part => !(part.forced && part.affected === 0))
        .map(part => {
          const action = `${part.type} ${part.value ? 'on' : 'off'}`
          const reason = part.reason ? ` (${part.reason})` : part.forced ? (part.value ? ' (needed for Enabled)' : ' (follows Access)') : ''
          if (part.affected === null) return `${action} for all ${this.tenantCountText}${reason}`
          if (part.affected === 0) return `${action}: already ${part.value ? 'on' : 'off'} for all ${this.knownCount} loaded tenants`
          const others = this.unloadedTenantCount ? ` (+ up to ${this.unloadedTenantCount} on other pages)` : ''
          return `${action}${reason} for ${part.affected} of ${this.knownCount} tenants${others}`
        })
        .join('; ')
    },
    setChoice(key, type, value) {
      this.selections[key][type] = value
    },
    apply() {
      if (!this.changes.length) return
      this.$emit('apply', this.changes)
    },
  },
}
</script>

<style>
.bulk-feature-dialog:not(.is-fullscreen) {
  width: calc(100vw - 32px) !important;
  max-width: 960px;
}
.bulk-feature-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}
.bulk-feature-search {
  width: 16rem;
  max-width: 100%;
}
.bulk-feature-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.bulk-feature-actions {
  margin-left: auto;
}
.bulk-feature-dialog .el-dialog__body {
  word-break: normal;
  padding-top: 10px;
  padding-bottom: 10px;
}
.bulk-feature-table-wrapper {
  max-height: calc(100vh - 330px);
  min-height: 200px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}
.bulk-feature-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fafafa;
  color: #909399;
  font-weight: 600;
  text-align: left;
  font-size: 12px;
}
.bulk-feature-table th,
.bulk-feature-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #ebeef5;
  white-space: nowrap;
  word-break: normal;
}
.bulk-feature-table .el-radio-group {
  display: inline-flex;
  flex-wrap: nowrap;
}
.bulk-feature-table .bulk-feature-off.is-active .el-radio-button__inner {
  background-color: #f56c6c;
  border-color: #f56c6c;
  box-shadow: -1px 0 0 0 #f56c6c;
}
.bulk-feature-table tr.is-changed td {
  background: #ecf5ff;
}
.bulk-feature-warning {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #b88230;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
}
.bulk-feature-review li {
  margin: 8px 0;
  word-break: normal;
}
/* Tablet: let the radio groups wrap instead of forcing a horizontal scroll */
@media (max-width: 1023px) {
  .bulk-feature-table th,
  .bulk-feature-table td {
    padding: 8px 6px;
  }
  .bulk-feature-table td.bulk-feature-name {
    white-space: normal;
  }
}
/* Phone: fullscreen dialog, each feature row becomes a stacked card */
@media (max-width: 767px) {
  .bulk-feature-dialog.is-fullscreen {
    display: flex;
    flex-direction: column;
  }
  .bulk-feature-dialog.is-fullscreen .el-dialog__body {
    flex: 1;
    overflow-y: auto;
  }
  .bulk-feature-dialog.is-fullscreen .bulk-feature-table-wrapper {
    max-height: none;
    overflow: visible;
    border: 0;
  }
  .bulk-feature-search {
    width: 100%;
  }
  .bulk-feature-table thead {
    display: none;
  }
  .bulk-feature-table,
  .bulk-feature-table tbody,
  .bulk-feature-table tr,
  .bulk-feature-table td {
    display: block;
    width: 100%;
  }
  .bulk-feature-table tr {
    display: grid;
    grid-template-columns: 96px 1fr;
    align-items: center;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  .bulk-feature-table td.bulk-feature-name,
  .bulk-feature-table td[colspan] {
    grid-column: 1 / -1;
  }
  .bulk-feature-table td {
    width: auto;
    min-width: 0;
    border-bottom: 0;
    padding: 4px 10px;
  }
  .bulk-feature-table tr.is-changed {
    background: #ecf5ff;
  }
  .bulk-feature-table tr.is-changed td {
    background: transparent;
  }
  .bulk-feature-table td.bulk-feature-name {
    padding-top: 8px;
    font-size: 14px;
  }
  .bulk-feature-table td[data-label]::before {
    content: attr(data-label);
    display: block;
    font-size: 11px;
    color: #909399;
    margin-bottom: 2px;
  }
  .bulk-feature-footer {
    flex-direction: column;
    align-items: stretch;
  }
  .bulk-feature-actions {
    display: flex;
    margin-left: 0;
  }
  .bulk-feature-actions .el-button {
    flex: 1;
  }
}
</style>
